/**
 * tr-quick-add — quick-add multi-variantes pour la fiche en Theme Blocks
 * (P1-01, 09·R1/R13). Remplace la modale Dawn (qui cherchait un <product-info>
 * disparu) : le lien « Choisir les options » de la carte devient un ouvreur de
 * <dialog> qui fetch la fiche produit et en extrait la buy-box
 * `[data-ref="productInfo"]` de `tr-product-information`.
 *
 * - Sans JS : le lien navigue vers la fiche (aucune régression).
 * - Modules de la buy-box réactivés (script[src] ré-appendés une seule fois) ;
 *   les custom elements (tr-product-form, tr-variant-picker…) s'upgradent seuls.
 * - Fermée sur `tr:cart-add` (le tiroir ou le toast prend le relais).
 * - Échec réseau : spinner libéré PUIS navigation vers la fiche (repli honnête,
 *   contrairement au bug FullStack qui bloquait le spinner).
 *
 * Script classique (chargé en defer par les grilles) : pas d'import ESM —
 * ponts `window.Traction.events` / catalogue @traction/events.
 */

(() => {
  if (customElements.get('tr-quick-add') !== undefined) return;

  const EVENTS = () => window.Traction?.events ?? {};
  const MODAL_ID = 'tr-quick-add-modal';

  /** Dialog singleton, créé au premier usage. */
  function getModal() {
    let dialog = document.getElementById(MODAL_ID);
    if (dialog !== null) return dialog;

    dialog = document.createElement('dialog');
    dialog.id = MODAL_ID;
    dialog.className = 'tr-quick-add-modal';

    dialog.innerHTML = `
      <div class="tr-quick-add-modal__panel">
        <button type="button" class="tr-quick-add-modal__close" data-ref="quickAddClose"></button>
        <div class="tr-quick-add-modal__content" data-ref="quickAddContent"></div>
        <a class="tr-quick-add-modal__details" data-ref="quickAddDetails" href="#"></a>
      </div>
    `;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-ref="quickAddClose"]') !== null) {
        dialog.close();
      }
    });

    dialog.addEventListener('close', () => {
      const content = dialog.querySelector('[data-ref="quickAddContent"]');
      if (content !== null) content.replaceChildren();
    });

    document.body.append(dialog);
    return dialog;
  }

  /* W-E (06·L11) : cache de fiches par URL, partagé par toutes les cartes de la
     page. Rouvrir le même produit ne refait plus un aller-retour réseau. */
  const markupCache = new Map();

  class TrQuickAdd extends HTMLElement {
    #controller = null;
    #fetchController = null;

    connectedCallback() {
      this.#controller = new AbortController();
      this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#fetchController?.abort();
      this.#controller = null;
      this.#fetchController = null;
    }

    #onClick = (event) => {
      const trigger = event.target.closest('[data-ref="quickAddTrigger"]');
      if (trigger === null) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.#open(trigger);
    };

    async #open(trigger) {
      if (trigger.getAttribute('aria-busy') === 'true') return;

      const url = this.dataset.productUrl ?? trigger.getAttribute('href') ?? '';
      if (url === '') return;

      trigger.setAttribute('aria-busy', 'true');
      trigger.classList.add('is-loading');

      try {
        let markup = markupCache.get(url);

        if (markup === undefined) {
          /* Une seule requête en vol par carte : un double clic annule la précédente. */
          this.#fetchController?.abort();
          this.#fetchController = new AbortController();

          const response = await fetch(url, { signal: this.#fetchController.signal });
          if (response.ok === false) throw new Error(String(response.status));

          markup = await response.text();
          markupCache.set(url, markup);
        }

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const info = parsed.querySelector('[data-ref="productInfo"]');
        if (info === null) throw new Error('buy-box introuvable');

        this.#prepare(info, parsed);

        const dialog = getModal();
        const content = dialog.querySelector('[data-ref="quickAddContent"]');
        const details = dialog.querySelector('[data-ref="quickAddDetails"]');

        dialog.setAttribute('aria-label', this.dataset.productTitle ?? '');
        if (details !== null) {
          details.href = url;
          details.textContent = this.dataset.stringDetails ?? '';
        }

        const closer = dialog.querySelector('[data-ref="quickAddClose"]');
        if (closer !== null) closer.setAttribute('aria-label', this.dataset.stringClose ?? '');

        content?.replaceChildren(...Array.from(info.childNodes));
        dialog.showModal();
        this.#armAutoClose(dialog);
      } catch (error) {
        /* Une requête annulée (démontage, double clic) n'est pas une erreur. */
        if (error?.name === 'AbortError') return;
        /* Repli honnête : la fiche complète (le spinner est libéré au finally). */
        window.location.assign(url);
      } finally {
        trigger.setAttribute('aria-busy', 'false');
        trigger.classList.remove('is-loading');
      }
    }

    /** Nettoie la buy-box et réactive ses modules (une seule fois par src). */
    #prepare(info, parsed) {
      for (const node of info.querySelectorAll('tr-sticky-atc, script[type="application/ld+json"], noscript, dialog')) {
        node.remove();
      }

      const seen = new Set(
        Array.from(document.querySelectorAll('script[src]')).map((node) => node.src)
      );

      for (const script of parsed.querySelectorAll('[data-ref="productInfo"] script[src], .tr-section-product script[src]')) {
        if (seen.has(script.src)) continue;
        seen.add(script.src);

        const fresh = document.createElement('script');
        fresh.src = script.src;
        if (script.type !== '') fresh.type = script.type;
        document.head.append(fresh);
      }

      /* Les scripts inline des blocs ne se ré-exécutent pas via innerHTML :
         on les recrée (petits ponts data → custom elements). */
      for (const script of info.querySelectorAll('script:not([src]):not([type="application/json"])')) {
        const fresh = document.createElement('script');
        fresh.textContent = script.textContent;
        if (script.type !== '') fresh.type = script.type;
        script.replaceWith(fresh);
      }
    }

    /** Ferme la modale dès qu'un ajout panier aboutit (pattern FS corrigé). */
    #armAutoClose(dialog) {
      const eventName = EVENTS().CART_ADD ?? 'tr:cart-add';

      const onAdd = () => {
        document.removeEventListener(eventName, onAdd);
        if (dialog.open === true) dialog.close();
      };

      document.addEventListener(eventName, onAdd);
      dialog.addEventListener('close', () => document.removeEventListener(eventName, onAdd), { once: true });
    }
  }

  customElements.define('tr-quick-add', TrQuickAdd);
})();
